/**
 * 金額計算邏輯
 * 使用 decimal.js 避免浮點數誤差
 * 所有計算函數接受 Decimal 或 number，返回 Decimal
 */
import { Decimal } from 'decimal.js';
import { roundMoney } from './rounding.js';
/**
 * 計算折扣金額
 *
 * @param originalPrice 原價（元，整數）
 * @param discountType 折扣類型：'PERCENTAGE'（百分比）或 'FIXED_AMOUNT'（固定金額）
 * @param discountValue 折扣值：
 *   - 百分比折扣：存 10 代表 10% off（打 9 折）
 *   - 固定金額：存元（整數）
 * @returns 折扣金額（Decimal，已進位為整數）
 *
 * @example
 * ```ts
 * calculateDiscountAmount(100, 'PERCENTAGE', 10) // 10 (10% off)
 * calculateDiscountAmount(100, 'FIXED_AMOUNT', 20) // 20
 * ```
 */
export function calculateDiscountAmount(originalPrice, discountType, discountValue) {
    const price = new Decimal(originalPrice);
    const value = new Decimal(discountValue);
    if (price.lte(0)) {
        return new Decimal(0);
    }
    if (discountType === 'PERCENTAGE') {
        // 百分比折扣：price * value / 100，然後進位
        const discountAmount = price.mul(value).div(100);
        const rounded = roundMoney(discountAmount);
        // 折扣不能超過原價
        return Decimal.min(rounded, price);
    }
    else {
        // 固定金額折扣：取 min(value, price)
        return Decimal.min(value, price);
    }
}
/**
 * 計算稅金
 *
 * @param subtotal 小計（元，整數）
 * @param taxRateBps 稅率（basis points），例如 500 代表 5%
 * @returns 稅金（Decimal，已進位為整數）
 *
 * @example
 * ```ts
 * calculateTaxAmount(100, 500) // 5 (5%)
 * calculateTaxAmount(228, 500) // 11 (228 * 5% = 11.4，四捨五入為 11)
 * ```
 */
export function calculateTaxAmount(subtotal, taxRateBps) {
    const subtotalDecimal = new Decimal(subtotal);
    const rateDecimal = new Decimal(taxRateBps);
    if (subtotalDecimal.lte(0) || rateDecimal.lte(0)) {
        return new Decimal(0);
    }
    // 計算稅金：subtotal * taxRateBps / 10000，然後進位
    const taxAmount = subtotalDecimal.mul(rateDecimal).div(10000);
    return roundMoney(taxAmount);
}
/**
 * 計算折扣後金額
 *
 * @param originalAmount 原價總額
 * @param discountAmount 折扣金額
 * @returns 折扣後金額（Decimal，已進位為整數，不小於 0）
 *
 * @example
 * ```ts
 * calculateDiscountedAmount(240, 24) // 216
 * calculateDiscountedAmount(100, 150) // 0 (折扣不能超過原價)
 * ```
 */
export function calculateDiscountedAmount(originalAmount, discountAmount) {
    const original = new Decimal(originalAmount);
    const discount = new Decimal(discountAmount);
    const result = original.sub(discount);
    // 確保不小於 0
    return Decimal.max(result, 0);
}
/**
 * 計算找零
 *
 * @param paidAmount 支付金額
 * @param totalAmount 總金額
 * @returns 找零金額（Decimal，已進位為整數，不小於 0）
 *
 * @example
 * ```ts
 * calculateChange(1000, 239) // 761
 * calculateChange(100, 150) // 0 (支付不足)
 * ```
 */
export function calculateChange(paidAmount, totalAmount) {
    const paid = new Decimal(paidAmount);
    const total = new Decimal(totalAmount);
    const change = paid.sub(total);
    // 確保不小於 0
    return Decimal.max(change, 0);
}
/**
 * 計算訂單項目總額
 *
 * @param quantity 數量
 * @param finalPrice 折扣後單價（元，整數）
 * @param taxAmount 稅金（元，整數）
 * @returns 總額（Decimal）：(quantity * finalPrice) + taxAmount
 *
 * @example
 * ```ts
 * calculateOrderItemTotal(2, 90, 9) // 189 (2 * 90 + 9)
 * ```
 */
export function calculateOrderItemTotal(quantity, finalPrice, taxAmount) {
    const qty = new Decimal(quantity);
    const price = new Decimal(finalPrice);
    const tax = new Decimal(taxAmount);
    return qty.mul(price).add(tax);
}
/**
 * 計算總金額（含四捨五入）
 *
 * @param items 項目列表，每個項目包含 price 和 quantity
 * @returns 總金額（Decimal）
 *
 * @example
 * ```ts
 * calculateTotal([
 *   { price: 120, quantity: 2 },
 *   { price: 100, quantity: 1 }
 * ]) // 340
 * ```
 */
export function calculateTotal(items) {
    return items.reduce((sum, item) => {
        const price = new Decimal(item.price);
        const quantity = new Decimal(item.quantity);
        return sum.add(price.mul(quantity));
    }, new Decimal(0));
}
/**
 * 購物車金額統一計算（全程整數「分」，最後一刻才顯示）
 * 業界法則：Store in minor units；稅金四捨五入到「元」後再加總，確保顯示與應付一致、找零無差 1。
 *
 * @param subtotalCents 小計（分）
 * @param discountCents 折扣（分）
 * @param taxRateBps 稅率（basis points），例如 500 = 5%
 * @returns 各項與應付總額（分）
 *
 * @example
 * // 可頌 300 元、折扣 30 元、稅 5%
 * calculateCartTotalInCents(30000, 3000, 500)
 * // => { preTaxCents: 27000, taxCents: 1350, taxDisplayCents: 1400, totalPayableCents: 28400 }
 */
export function calculateCartTotalInCents(subtotalCents, discountCents, taxRateBps) {
    const preTaxCents = Math.max(0, Math.round(subtotalCents) - Math.round(discountCents));
    if (preTaxCents <= 0) {
        return {
            subtotalCents: Math.round(subtotalCents),
            discountCents: Math.round(discountCents),
            preTaxCents: 0,
            taxCents: 0,
            taxDisplayCents: 0,
            totalPayableCents: 0,
        };
    }
    if (taxRateBps <= 0) {
        return {
            subtotalCents: Math.round(subtotalCents),
            discountCents: Math.round(discountCents),
            preTaxCents,
            taxCents: 0,
            taxDisplayCents: 0,
            totalPayableCents: preTaxCents,
        };
    }
    // 稅金：先乘後除，全程整數（分）
    const taxCents = Math.round((preTaxCents * taxRateBps) / 10000);
    // 稅金四捨五入到「元」，使 UI 顯示與應付一致，避免找零差 1
    const taxDisplayCents = Math.round(taxCents / 100) * 100;
    const totalPayableCents = preTaxCents + taxDisplayCents;
    return {
        subtotalCents: Math.round(subtotalCents),
        discountCents: Math.round(discountCents),
        preTaxCents,
        taxCents,
        taxDisplayCents,
        totalPayableCents,
    };
}
/**
 * 將 Decimal 轉換為整數（number）
 * 用於需要 number 類型的場景（如 API 回應）
 * 會先四捨五入到整數
 *
 * @param amount 金額（Decimal）
 * @returns 整數（number）
 */
export function toInteger(amount) {
    return amount.round().toNumber();
}
/**
 * 將 Decimal 轉換為數字（number，可能含小數）
 * 用於需要 number 類型的場景（如 API 回應）
 *
 * @param amount 金額（Decimal）
 * @returns 數字（number）
 */
export function toNumber(amount) {
    return amount.toNumber();
}
